import React, { useContext } from "react";
import ReactMarkdown from "react-markdown";

import { ProcedureContext } from "@/context/ProcedureContext";
import { UniqueIdentifier } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragHandle } from "@mui/icons-material";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import rehypeRaw from "rehype-raw";

import { tokens } from "@/components/Application/theme";
import { useFetchChecklist } from "@/components/Procedures/API/ChecklistQueries";
import { ChecklistRow } from "@/components/Procedures/Checklist/ChecklistRow";
import { StepManualMeasurements } from "@/components/Procedures/Checklist/Step/StepManualMeasurements";
import { getTextColorForBackground } from "@/components/Procedures/Checklist/utils/Helpers";
import { SatosLiveTagChip } from "@/components/Procedures/Components/Common/SatosLiveTagChip";
import { CheckpointType } from "@/components/Procedures/Types/ManualChecklistTypes";
import { getStepType } from "@/components/Procedures/helpers/getStepType";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";

import { useFetchGroups } from "@/service/GroupQueries";

type Props = {
	i: number;
	id: UniqueIdentifier;
	step: ProcedureNodeStep;
	checklistId: number;
	importedChecklist: boolean;
	stepNumber?: number;
};

const PreviewStep = ({
	i,
	id,
	step,
	checklistId,
	importedChecklist,
	stepNumber,
}: Props) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
		overIndex,
		activeIndex,
	} = useSortable({ id: id });

	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const { activeStep, setActiveStep } = useContext(ProcedureContext);
	const style = {
		transform: CSS.Transform.toString(transform),
		transition: `${transition}, background-color .3s ease`,
		zIndex: 10,
		position: isDragging ? "relative" : ("static" as "relative" | "static"),
		boxShadow: isDragging ? "0 0 10px 0 rgba(0, 0, 0, 0.2)" : "none",
		backgroundColor: step?.BackgroundColor
			? step.BackgroundColor
			: +i % 2 === 0
				? "lightgrey"
				: "white",
		color: step?.BackgroundColor
			? getTextColorForBackground(step?.BackgroundColor)
			: "black",
		pointer: isDragging ? "grabbing" : "pointer",
		border:
			i === activeStep
				? `2px dashed ${colors.yellowAccent[300]}`
				: "2px solid transparent",
		display: "flex",
		flexDirection: "row",
		minHeight: 82,
		gap: 3,
		justifyContent: "space-between",
		borderRadius: 2,
		p: 2,
	};

	const { data: checklist } = useFetchChecklist(checklistId);
	const { data: groups } = useFetchGroups();

	const stepHasGroupId = checklist?.Steps?.some((step) => step.GroupId);

	const getGroupNameById = (groupId: number) => {
		const group = groups?.find((group) => group?.Id === groupId);
		return group?.Name;
	};

	if (importedChecklist) {
		return (
			<section className="procedure-wrapper">
				<Box className="procedure-wrapper-inner">
					<Box className="grid-wrapper">
						<ChecklistRow
							step={step}
							activeSchemeId={checklistId}
							checklist={checklist}
						/>
					</Box>
				</Box>
			</section>
		);
	}

	return (
		<Stack
			ref={setNodeRef}
			{...attributes}
			{...listeners}
			className="procedure-wrapper-inner"
			sx={{
				...style,
				pointer: isDragging ? "grab" : "grabbing",
			}}
			onClick={() => {
				setActiveStep(+i);
			}}
		>
			<>
				<DragHandle
					data-swapy-handle
					fontSize="small"
					sx={{ cursor: isDragging ? "grabbing" : "grab" }}
				/>
				<Typography sx={{ fontWeight: 600 }}>
					{stepNumber ? `${stepNumber}.` : ""}
				</Typography>
				<Box
					display="flex"
					flexDirection="column"
					flex={"0 0 75%"}
					sx={{ wordWrap: "break-word" }}
				>
					<ReactMarkdown rehypePlugins={[rehypeRaw]} children={step?.Value} />
					{step?.CheckpointType === CheckpointType.ManualMeasurement &&
						step?.ManualReading && (
							<Box my={2}>
								<StepManualMeasurements
									step={step}
									activeSchemeId={checklistId}
									isDisabled={true}
								/>
							</Box>
						)}
					{step?.CheckpointType === CheckpointType.SATOSLive &&
						step?.ListenerTags?.map((tag) => (
							<SatosLiveTagChip key={tag.Id} label={<p>{tag.Name}</p>} />
						))}
				</Box>
				{stepHasGroupId && (
					<Box flex={"0 1 10%"}>{getGroupNameById(step?.GroupId)}</Box>
				)}
				<Box width={40}>{getStepType(step?.CheckpointType)}</Box>
			</>
		</Stack>
	);
};

export default PreviewStep;
